import React, { useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { fetchTemplateById } from '../utils/templateService';
import { forkTemplateToDocument } from '../utils/documentService';
import {
  ArrowLeftIcon,
  SparklesIcon,
  LightBulbIcon,
  ViewColumnsIcon,
  HeartIcon,
  PencilIcon,
  UserGroupIcon,
  ClockIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

interface Template {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  features: string[];
  estimatedTime: string;
  maxParticipants: number;
  category: 'collaboration' | 'design' | 'planning' | 'creative';
}

const templates: Template[] = [
  {
    id: 'brainstorming',
    name: 'Brainstorming Board',
    description: 'Generate and organize ideas with mind maps, sticky notes, and visual thinking tools.',
    icon: LightBulbIcon,
    color: 'from-yellow-400 to-orange-500',
    features: ['Mind mapping', 'Sticky notes', 'Idea clustering', 'Voting system'],
    estimatedTime: '30-60 min',
    maxParticipants: 10,
    category: 'collaboration'
  },
  {
    id: 'kanban',
    name: 'Kanban Board',
    description: 'Manage tasks and workflows with drag-and-drop columns and progress tracking.',
    icon: ViewColumnsIcon,
    color: 'from-blue-500 to-indigo-600',
    features: ['Task management', 'Progress tracking', 'Team collaboration', 'Deadline management'],
    estimatedTime: '15-45 min',
    maxParticipants: 15,
    category: 'planning'
  },
  {
    id: 'moodboard',
    name: 'Mood Board',
    description: 'Create visual inspiration boards with images, colors, and design elements.',
    icon: HeartIcon,
    color: 'from-pink-400 to-rose-500',
    features: ['Image upload', 'Color palettes', 'Design inspiration', 'Style guides'],
    estimatedTime: '20-40 min',
    maxParticipants: 8,
    category: 'design'
  },
  {
    id: 'whiteboard',
    name: 'Whiteboard',
    description: 'Free-form drawing and sketching for diagrams, wireframes, and visual notes.',
    icon: PencilIcon,
    color: 'from-green-400 to-emerald-500',
    features: ['Free drawing', 'Shapes & tools', 'Real-time collaboration', 'Export options'],
    estimatedTime: 'Flexible',
    maxParticipants: 12,
    category: 'creative'
  },
  {
    id: 'retrospective',
    name: 'Sprint Retrospective',
    description: 'Team reflection and improvement sessions with structured feedback collection.',
    icon: UserGroupIcon,
    color: 'from-purple-400 to-violet-500',
    features: ['Feedback collection', 'Action items', 'Team insights', 'Progress tracking'],
    estimatedTime: '45-90 min',
    maxParticipants: 12,
    category: 'collaboration'
  },
  {
    id: 'design-sprint',
    name: 'Design Sprint',
    description: 'Structured design process with user research, ideation, and prototyping phases.',
    icon: SparklesIcon,
    color: 'from-cyan-400 to-blue-500',
    features: ['User research', 'Rapid prototyping', 'User testing', 'Design validation'],
    estimatedTime: '2-5 days',
    maxParticipants: 8,
    category: 'design'
  }
];

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId?: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Templates' },
    { id: 'collaboration', name: 'Collaboration' },
    { id: 'design', name: 'Design' },
    { id: 'planning', name: 'Planning' },
    { id: 'creative', name: 'Creative' }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateSelect = async (templateId: string) => {
    // If launched from a team context, immediately fork into a team document
    if (teamId && currentUser?.uid) {
      try {
        const tplData = await fetchTemplateById(templateId);
        if (tplData) {
          const newDocId = await forkTemplateToDocument(tplData, currentUser.uid, teamId);
          navigate(`/editor/${newDocId}`);
          return;
        }
      } catch (err) {
        console.error('Error forking template:', err);
      }
      // Fallback – open default template (read-only) if fetch/fork failed
      navigate(`/editor/${templateId}`);
    } else {
    navigate(`/editor/${templateId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Choose a Template</h1>
                <p className="text-sm text-gray-600">Select the perfect template for your team meeting</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <SparklesIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Filter by:</span>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="appearance-none px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white shadow-sm pr-10"
                  style={{ minWidth: 140 }}
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <svg className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              onClick={() => handleTemplateSelect(template.id)}
            >
              {/* Template Header */}
              <div className={`bg-gradient-to-r ${template.color} p-6 rounded-t-xl`}>
                <div className="flex items-center justify-between">
                  <template.icon className="w-8 h-8 text-white" />
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="w-4 h-4 text-white/80" />
                    <span className="text-white/90 text-sm">{template.estimatedTime}</span>
                  </div>
                </div>
                <h3 className="text-white font-bold text-lg mt-3">{template.name}</h3>
                <p className="text-white/90 text-sm mt-1">{template.description}</p>
              </div>

              {/* Template Content */}
              <div className="p-6">
                {/* Features */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Features</h4>
                  <div className="flex flex-wrap gap-1">
                    {template.features.slice(0, 3).map((feature, index) => (
                      <span
                        key={index}
                        className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                    {template.features.length > 3 && (
                      <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                        +{template.features.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <UsersIcon className="w-4 h-4" />
                    <span>Up to {template.maxParticipants}</span>
                  </div>
                  <span className="capitalize">{template.category}</span>
                </div>

                {/* CTA Button */}
                <button className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 px-4 rounded-lg font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 group-hover:shadow-md">
                  Use This Template
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <SparklesIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Quick Start Guide */}
        <div className="mt-12 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Start Guide</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Choose Template</h4>
                <p className="text-sm text-gray-600">Select a template that matches your meeting goals</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Invite Team</h4>
                <p className="text-sm text-gray-600">Share the meeting link with your team members</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Start Collaborating</h4>
                <p className="text-sm text-gray-600">Begin your visual collaboration session</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TemplatesPage; 